/**
 * ホーム画面の操作（SPEC §7.1）。
 *
 * どれも「ローカルへ楽観反映」と「キューへ積む」を 1 トランザクションで行い、
 * その後 flush を起動する。片方だけ成立して不整合になる状態を作らない。
 */
import { fifoPurchaseId } from '../../shared/fifo';
import type { ItemRow, LotRow, PurchaseRow } from '../../shared/types';
import { findItemByBarcode } from './barcode';
import { today } from './date';
import { db } from './db';
import { clearShoppingFlags } from './settings';
import { enqueue, flushSoon } from './sync';
import { uuid } from './uuid';

const nowIso = (): string => new Date().toISOString();

/* ------------------------------------------------------------------ */
/* 品目                                                                */
/* ------------------------------------------------------------------ */

export interface NewItemInput {
  name: string;
  category: string | null;
  defaultDurationDays: number;
  /** スキャンした未知バーコードから品目を新規作成する場合のみ（SPEC §11.2） */
  barcode?: string | null;
}

export async function addItem(input: NewItemInput): Promise<ItemRow> {
  const maxSort = (await db.items.toArray()).reduce((m, i) => Math.max(m, i.sort_order), 0);
  const at = nowIso();
  const row: ItemRow = {
    id: uuid(),
    name: input.name,
    category: input.category,
    barcode: input.barcode ?? null,
    default_duration_days: input.defaultDurationDays,
    note: null,
    sort_order: maxSort + 1,
    created_at: at,
    updated_at: at,
    deleted_at: null,
  };

  await db.transaction('rw', db.items, db.syncQueue, async () => {
    await db.items.put(row);
    await enqueue({
      method: 'POST',
      path: '/api/items',
      label: `${row.name} を追加`,
      body: {
        id: row.id,
        name: row.name,
        category: row.category,
        barcode: row.barcode,
        default_duration_days: row.default_duration_days,
        sort_order: row.sort_order,
      },
    });
  });

  flushSoon();
  return row;
}

/* ------------------------------------------------------------------ */
/* 購入                                                                */
/* ------------------------------------------------------------------ */

export interface NewPurchaseInput {
  item: ItemRow;
  qty: number;
  totalPrice: number;
  purchasedAt: string; // YYYY-MM-DD
  store: string | null;
}

export async function addPurchase(input: NewPurchaseInput): Promise<PurchaseRow> {
  const at = nowIso();
  const row: PurchaseRow = {
    id: uuid(),
    item_id: input.item.id,
    qty: input.qty,
    total_price: input.totalPrice,
    purchased_at: input.purchasedAt,
    store: input.store,
    created_at: at,
    updated_at: at,
    deleted_at: null,
  };

  await db.transaction('rw', db.purchases, db.syncQueue, async () => {
    await db.purchases.put(row);
    await enqueue({
      method: 'POST',
      path: '/api/purchases',
      label: `${input.item.name} を購入（${input.qty}個 / ${input.totalPrice}円）`,
      body: {
        id: row.id,
        item_id: row.item_id,
        qty: row.qty,
        total_price: row.total_price,
        purchased_at: row.purchased_at,
        store: row.store,
      },
    });
  });

  // 買えたので買い物リストの「今回は除外 / 手動追加」を解除する（SPEC §6.4）
  await clearShoppingFlags(input.item.id);
  flushSoon();
  return row;
}

export async function deletePurchase(purchase: PurchaseRow, itemName: string): Promise<void> {
  await db.transaction('rw', db.purchases, db.lots, db.syncQueue, async () => {
    await db.purchases.delete(purchase.id);
    // SPEC §4.4: purchase 削除時、紐付く lot は残して purchase_id を NULL にする
    const linked = await db.lots.where('purchase_id').equals(purchase.id).toArray();
    if (linked.length) {
      await db.lots.bulkPut(linked.map((l) => ({ ...l, purchase_id: null })));
    }
    await enqueue({
      method: 'DELETE',
      path: `/api/purchases/${purchase.id}`,
      label: `${itemName} の購入記録を取り消し`,
    });
  });

  flushSoon();
}

/* ------------------------------------------------------------------ */
/* ロット（開封 / 使い切り）                                             */
/* ------------------------------------------------------------------ */

/**
 * 開封（SPEC §7.1: 確認なし即実行 → Undo スナックバー）。
 *
 * purchase_id はサーバの FIFO 割当が正だが、レスポンスを待たずに
 * 「購入記録なし」バッジを正しく出したいのでローカルでも同じ規則で先読みする。
 */
export async function openLot(item: ItemRow): Promise<LotRow> {
  const [purchases, lots] = await Promise.all([
    db.purchases.where('item_id').equals(item.id).toArray(),
    db.lots.where('item_id').equals(item.id).toArray(),
  ]);

  const at = nowIso();
  const row: LotRow = {
    id: uuid(),
    item_id: item.id,
    purchase_id: fifoPurchaseId(purchases, lots, item.id),
    opened_at: today(),
    finished_at: null,
    created_at: at,
    updated_at: at,
    deleted_at: null,
  };

  await db.transaction('rw', db.lots, db.syncQueue, async () => {
    await db.lots.put(row);
    await enqueue({
      method: 'POST',
      path: '/api/lots',
      label: `${item.name} を開封`,
      body: { id: row.id, item_id: row.item_id, opened_at: row.opened_at },
    });
  });

  flushSoon();
  return row;
}

/** 開封の取り消し（Undo）。soft delete なので在庫は自動的に戻る。 */
export async function undoOpenLot(lot: LotRow, itemName: string): Promise<void> {
  await db.transaction('rw', db.lots, db.syncQueue, async () => {
    await db.lots.delete(lot.id);
    await enqueue({
      method: 'DELETE',
      path: `/api/lots/${lot.id}`,
      label: `${itemName} の開封を取り消し`,
    });
  });

  flushSoon();
}

/** 使い切った（SPEC §7.1: finished_at = 今日） */
export async function finishLot(lot: LotRow, itemName: string): Promise<LotRow> {
  const finishedAt = today();
  const row: LotRow = { ...lot, finished_at: finishedAt, updated_at: nowIso() };

  await db.transaction('rw', db.lots, db.syncQueue, async () => {
    await db.lots.put(row);
    await enqueue({
      method: 'PATCH',
      path: `/api/lots/${lot.id}`,
      label: `${itemName} を使い切り`,
      body: { finished_at: finishedAt },
    });
  });

  flushSoon();
  return row;
}

/** 使い切りの取り消し（Undo）→ 使用中に戻す */
export async function undoFinishLot(lot: LotRow, itemName: string): Promise<void> {
  const row: LotRow = { ...lot, finished_at: null, updated_at: nowIso() };

  await db.transaction('rw', db.lots, db.syncQueue, async () => {
    await db.lots.put(row);
    await enqueue({
      method: 'PATCH',
      path: `/api/lots/${lot.id}`,
      label: `${itemName} の使い切りを取り消し`,
      body: { finished_at: null },
    });
  });

  flushSoon();
}

/* ------------------------------------------------------------------ */
/* 品目の編集・削除（SPEC §7.4）                                          */
/* ------------------------------------------------------------------ */

export interface ItemPatch {
  name?: string;
  category?: string | null;
  /** SPEC §11.2: 紐付けは値、解除は null。同じコードが他品目にあるとサーバが 409 */
  barcode?: string | null;
  default_duration_days?: number;
  note?: string | null;
  sort_order?: number;
}

export async function updateItem(item: ItemRow, patch: ItemPatch): Promise<void> {
  const row: ItemRow = { ...item, ...patch, updated_at: nowIso() };

  await db.transaction('rw', db.items, db.syncQueue, async () => {
    await db.items.put(row);
    await enqueue({
      method: 'PATCH',
      path: `/api/items/${item.id}`,
      label: `${row.name} の設定を変更`,
      body: patch,
    });
  });

  flushSoon();
}

/**
 * バーコードの紐付け（SPEC §11.2）。
 *
 * 既に他品目に紐付いているコードはサーバが 409 を返す（SPEC §4.5）。
 * キューに積んでから失敗させても直しようがないので、送る前に同じ判定で弾く。
 */
export async function linkBarcode(item: ItemRow, barcode: string): Promise<void> {
  const others = (await db.items.toArray()).filter((i) => i.id !== item.id);
  const owner = findItemByBarcode(others, barcode);
  if (owner) {
    throw new Error(`このバーコードは「${owner.name}」に紐付いています。先に解除してください。`);
  }
  await updateItem(item, { barcode });
}

/** 紐付けの解除（SPEC §7.4）。付け替えはこの解除→再紐付けの 2 ステップで行う。 */
export async function unlinkBarcode(item: ItemRow): Promise<void> {
  await updateItem(item, { barcode: null });
}

/**
 * 品目の削除（SPEC §4.5: 配下の purchases / lots も連鎖 soft delete）。
 * 破壊的操作なので、呼び出し側で件数を提示した確認ダイアログを必ず挟むこと。
 */
export async function deleteItem(item: ItemRow): Promise<void> {
  await db.transaction('rw', db.items, db.purchases, db.lots, db.syncQueue, async () => {
    const [purchaseIds, lotIds] = await Promise.all([
      db.purchases.where('item_id').equals(item.id).primaryKeys(),
      db.lots.where('item_id').equals(item.id).primaryKeys(),
    ]);
    await Promise.all([
      db.purchases.bulkDelete(purchaseIds),
      db.lots.bulkDelete(lotIds),
      db.items.delete(item.id),
    ]);
    await enqueue({
      method: 'DELETE',
      path: `/api/items/${item.id}`,
      label: `${item.name} を削除`,
    });
  });

  flushSoon();
}

/* ------------------------------------------------------------------ */
/* ロットの編集（SPEC §7.4: 記録忘れのリカバリ）                          */
/* ------------------------------------------------------------------ */

export interface LotPatch {
  opened_at?: string;
  finished_at?: string | null;
}

export async function updateLot(lot: LotRow, patch: LotPatch, itemName: string): Promise<void> {
  const row: LotRow = { ...lot, ...patch, updated_at: nowIso() };

  await db.transaction('rw', db.lots, db.syncQueue, async () => {
    await db.lots.put(row);
    await enqueue({
      method: 'PATCH',
      path: `/api/lots/${lot.id}`,
      label: `${itemName} のロット記録を修正`,
      body: patch,
    });
  });

  flushSoon();
}

export async function deleteLot(lot: LotRow, itemName: string): Promise<void> {
  await db.transaction('rw', db.lots, db.syncQueue, async () => {
    await db.lots.delete(lot.id);
    await enqueue({
      method: 'DELETE',
      path: `/api/lots/${lot.id}`,
      label: `${itemName} のロット記録を削除`,
    });
  });

  flushSoon();
}

/* ------------------------------------------------------------------ */
/* 購入の編集（SPEC §7.4）                                                */
/* ------------------------------------------------------------------ */

export interface PurchasePatch {
  qty?: number;
  total_price?: number;
  purchased_at?: string;
  store?: string | null;
}

/**
 * 購入の編集。qty を割当済みロット数より減らすとサーバが 409 を返す（SPEC §4.5）ため、
 * 送る前にローカルでも同じ判定をして弾く。
 */
export async function updatePurchase(
  purchase: PurchaseRow,
  patch: PurchasePatch,
  itemName: string,
): Promise<void> {
  if (patch.qty !== undefined) {
    const allocated = await db.lots.where('purchase_id').equals(purchase.id).count();
    if (patch.qty < allocated) {
      throw new Error(`開封済み数より少なくできません（開封済み ${allocated} 個）`);
    }
  }

  const row: PurchaseRow = { ...purchase, ...patch, updated_at: nowIso() };

  await db.transaction('rw', db.purchases, db.syncQueue, async () => {
    await db.purchases.put(row);
    await enqueue({
      method: 'PATCH',
      path: `/api/purchases/${purchase.id}`,
      label: `${itemName} の購入記録を修正`,
      body: patch,
    });
  });

  flushSoon();
}
